/**
 * The brand half of the sign-in screen. Passive: nothing here is interactive, and nothing here
 * moves — it exists to say which product this is and to stop the form reading as a bare template.
 *
 * The artwork is geometry, not illustration: a faint dot grid, two rings bleeding off the corner,
 * and an abstract echo of the dashboard's own KPI-card-plus-bars shape. All of it is one colour
 * (`brand.base`) at low opacity over the sage ground, so it reads as texture rather than as a
 * picture competing with the form. No photography, no gradients, no glass.
 *
 * Hidden below `md`: on a phone the brand is the lockup above the form, and a decorative panel
 * would just push the password field off the screen.
 */

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { RememberedShop } from '@pos/shared';
import { neutralBrand } from '@pos/tokens';

import { ShopMark } from '@/shared/ui/ShopMark';

/** Relative bar heights. Deliberately uneven — a flat row of bars looks like a loading state. */
const BARS = [38, 56, 30, 72, 88, 62, 96];

export function BrandPanel({ shop }: { shop: RememberedShop | null }) {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        position: 'relative',
        overflow: 'hidden',
        flexDirection: 'column',
        justifyContent: 'space-between',
        bgcolor: 'var(--pos-brand-subtle)',
        color: 'var(--pos-brand-subtle-text)',
        px: { md: 6, lg: 8 },
        py: { md: 6, lg: 7 },
      }}
    >
      <BackdropGeometry />

      {/* The remembered shop over the door, or the product's own name on a device that has
          never signed in. Neither is ever another shop's — a Diamond till does not open on
          OriTech's lockup. */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ position: 'relative', alignSelf: 'flex-start' }}>
        <ShopMark themeCode={shop?.themeCode ?? null} neutral={shop === null} size={34} />
        <Typography sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: 22, lineHeight: 1 }}>
          {shop?.name ?? neutralBrand.dashboard}
        </Typography>
      </Stack>

      <Box sx={{ position: 'relative', maxWidth: 420 }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, maxWidth: '22ch' }}
        >
          Everything your shop needs to sell, track, and grow.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2.5, opacity: 0.8, maxWidth: '42ch' }}>
          Sales, stock, M-Pesa and cash — one till, one ledger, one place to see what happened.
        </Typography>
      </Box>

      <DashboardSketch />
    </Box>
  );
}

/** Grid and rings. `aria-hidden` throughout: none of it carries meaning. */
function BackdropGeometry() {
  return (
    <Box aria-hidden sx={{ position: 'absolute', inset: 0, color: 'var(--pos-brand-base)' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.09,
          backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: -180,
          right: -220,
          width: 620,
          height: 620,
          borderRadius: '50%',
          border: '1px solid currentColor',
          opacity: 0.14,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          right: -120,
          width: 400,
          height: 400,
          borderRadius: '50%',
          border: '1px solid currentColor',
          opacity: 0.1,
        }}
      />
    </Box>
  );
}

/**
 * An abstract restatement of the Home screen's own shapes — a stat block and a bar series. Close
 * enough to say "this is an operational product", far enough from a screenshot that it will not
 * go stale the next time the dashboard changes.
 */
function DashboardSketch() {
  return (
    <Stack
      aria-hidden
      direction="row"
      spacing={2.5}
      alignItems="flex-end"
      sx={{ position: 'relative', color: 'var(--pos-brand-base)' }}
    >
      <Box
        sx={{
          flex: '0 0 176px',
          p: 2,
          borderRadius: 'var(--pos-radius-card)',
          bgcolor: 'var(--pos-surface-raised)',
          border: '1px solid currentColor',
          borderColor: 'var(--pos-border-subtle)',
        }}
      >
        <Box sx={{ height: 7, width: 58, borderRadius: 4, bgcolor: 'currentColor', opacity: 0.3 }} />
        <Box sx={{ mt: 1.75, height: 15, width: 104, borderRadius: 4, bgcolor: 'currentColor', opacity: 0.65 }} />
        <Box sx={{ mt: 1.25, height: 7, width: 78, borderRadius: 4, bgcolor: 'currentColor', opacity: 0.22 }} />
      </Box>

      <Stack direction="row" spacing={1.25} alignItems="flex-end" sx={{ height: 104, pb: 0.5 }}>
        {BARS.map((height, index) => (
          <Box
            key={index}
            sx={{
              width: 15,
              height: `${height}%`,
              borderRadius: '5px 5px 2px 2px',
              bgcolor: 'currentColor',
              opacity: 0.26,
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
